import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { reverseRecord } from "./records-utils";

export enum CustomerFacingReturnStatus {
  COMPLETE = "complete",
  PENDING = "pending",
  ON_THE_WAY = "on_the_way",
  REJECTED = "rejected",
  NOT_SHIPPED = "not_shipped",
}

export const customerFacingReturnStatusToDisplayText: Record<
  ReturnStatus,
  CustomerFacingReturnStatus
> = {
  [ReturnStatus.COMPLETE]: CustomerFacingReturnStatus.COMPLETE,
  [ReturnStatus.DELIVERED]: CustomerFacingReturnStatus.PENDING,
  [ReturnStatus.IN_TRANSIT]: CustomerFacingReturnStatus.ON_THE_WAY,
  [ReturnStatus.NEEDS_REVIEW]: CustomerFacingReturnStatus.PENDING,
  [ReturnStatus.IN_REVIEW]: CustomerFacingReturnStatus.PENDING,
  [ReturnStatus.OPEN]: CustomerFacingReturnStatus.PENDING,
  [ReturnStatus.REJECTED]: CustomerFacingReturnStatus.REJECTED,
  [ReturnStatus.FLAGGED]: CustomerFacingReturnStatus.PENDING,
  [ReturnStatus.PRE_SHIPMENT]: CustomerFacingReturnStatus.NOT_SHIPPED,
  [ReturnStatus.DELETED]: CustomerFacingReturnStatus.COMPLETE,
};

export const customerFacingReturnStatusToBadgeColor: Record<
  CustomerFacingReturnStatus,
  PillTheme
> = {
  [CustomerFacingReturnStatus.COMPLETE]: PillTheme.SUCCESS,
  [CustomerFacingReturnStatus.PENDING]: PillTheme.NEUTRAL_BLUE,
  [CustomerFacingReturnStatus.ON_THE_WAY]: PillTheme.NEUTRAL_BLUE,
  [CustomerFacingReturnStatus.REJECTED]: PillTheme.DANGER,
  [CustomerFacingReturnStatus.NOT_SHIPPED]: PillTheme.NEUTRAL_YELLOW,
};

export const returnStatusToDisplayText: Record<ReturnStatus, string> = {
  [ReturnStatus.COMPLETE]: "Complete",
  [ReturnStatus.DELIVERED]: "Delivered",
  [ReturnStatus.IN_TRANSIT]: "In transit",
  [ReturnStatus.NEEDS_REVIEW]: "Needs review",
  [ReturnStatus.IN_REVIEW]: "In review",
  [ReturnStatus.OPEN]: "Open",
  [ReturnStatus.REJECTED]: "Rejected",
  [ReturnStatus.FLAGGED]: "Flagged",
  [ReturnStatus.PRE_SHIPMENT]: "Pre-shipment",
  [ReturnStatus.DELETED]: "Reset",
};

export const isReturnStatusDisplayedRejected: Record<ReturnStatus, boolean> = {
  [ReturnStatus.OPEN]: false,
  [ReturnStatus.NEEDS_REVIEW]: false,
  [ReturnStatus.IN_TRANSIT]: false,
  [ReturnStatus.DELIVERED]: false,
  [ReturnStatus.COMPLETE]: false,
  [ReturnStatus.IN_REVIEW]: false,
  [ReturnStatus.FLAGGED]: false,
  [ReturnStatus.PRE_SHIPMENT]: false,
  [ReturnStatus.REJECTED]: true,
  [ReturnStatus.DELETED]: true,
};

export const inferredReturnStatusFromDisplayText = reverseRecord(
  returnStatusToDisplayText,
);

export const returnStatusToBadgeColor: Record<ReturnStatus, PillTheme> = {
  [ReturnStatus.COMPLETE]: PillTheme.SUCCESS,
  [ReturnStatus.DELIVERED]: PillTheme.SUCCESS,
  [ReturnStatus.IN_TRANSIT]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.NEEDS_REVIEW]: PillTheme.NEUTRAL_YELLOW,
  [ReturnStatus.IN_REVIEW]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.OPEN]: PillTheme.NEUTRAL_YELLOW,
  [ReturnStatus.REJECTED]: PillTheme.DANGER,
  [ReturnStatus.FLAGGED]: PillTheme.DANGER,
  [ReturnStatus.PRE_SHIPMENT]: PillTheme.NEUTRAL_BLUE,
  [ReturnStatus.DELETED]: PillTheme.NEUTRAL_YELLOW,
};

export function pillThemeToBadgeColor(
  theme: PillTheme,
): "success" | "blue" | "warning" | "error" | "gray" {
  switch (theme) {
    case PillTheme.SUCCESS:
      return "success";
    case PillTheme.NEUTRAL_BLUE:
      return "blue";
    case PillTheme.NEUTRAL_YELLOW:
      return "warning";
    case PillTheme.DANGER:
      return "error";
    default:
      return "gray";
  }
}

// Backward-compat re-export: ReturnStatus moved to redo-return-core-common, but
// consumers (e.g. the loop integration) still import it from here. Re-export so
// they resolve at runtime without each needing a new BUILD dep.
export { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
